// src/components/niveles/botones/SubirNivelButton.jsx
import { Button } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { subirNivelServices } from "../../../services/subirNivel";
import { setInformacion } from "../../../store/slices/usuarios";

/**
 * ✅ SubirNivelButton (FIX COMPLETO + AUTO MOLINO ROBUSTO)
 * - Soporta molino usando endpoint dedicado: /api/molino/subirNivel
 * - Auto-detecta molino por:
 *    mode === "molino"
 *    selector.isMolino === true
 *    selector.type/kind/nombre === "molino"
 *    fallback: id === 20
 * - Valida requisitos ANTES de llamar al backend (inventario + CF/HUESO/VIPHUESO)
 * - Si faltan recursos, muestra detalle exacto de cuánto falta
 *
 * Props:
 * - selector: objeto con { id, type?, kind?, nombre?, isMolino? }
 * - reqs: requisitos del nivel a validar (ej: { Energy:30, Carbon:120, Egg:10, Milk:10, hueso:4000 })
 * - setMensaje, setError, setOpen: handlers de tu UI de alertas
 * - mode: "auto" | "molino" | "land"
 */
export const SubirNivelButton = ({
  selector,
  reqs = null,
  setMensaje,
  setError,
  setOpen,
  mode = "auto",
}) => {
  const dispatch = useDispatch();
  const { informacion } = useSelector((state) => state.userSlice);

  const [loading, setLoading] = useState(false);

  const id = Number(selector?.id ?? 0);

  // =========================
  // ✅ Detectar si es Molino (ROBUSTO)
  // =========================
  const isMolino = useMemo(() => {
    if (mode === "molino") return true;
    if (mode === "land") return false;

    // mode === "auto"
    if (selector?.isMolino === true) return true;

    const type = String(selector?.type || selector?.kind || selector?.nombre || "")
      .trim()
      .toLowerCase();

    if (type === "molino" || type === "mill") return true;

    // fallback legacy si usas id 20 como molino
    if (id === 20) return true;

    return false;
  }, [mode, selector, id]);

  // =========================
  // ✅ Inventario -> Map
  // =========================
  const invMap = useMemo(() => {
    const inv = Array.isArray(informacion?.inventario) ? informacion.inventario : [];
    const map = new Map();
    for (const row of inv) {
      const k = String(row?.item || "").trim();
      const v = Number(row?.cantidad || 0);
      if (k) map.set(k, Number.isFinite(v) ? v : 0);
    }
    return map;
  }, [informacion?.inventario]);

  // =========================
  // ✅ Balances (CF/HUESO/VIPHUESO)
  // =========================
  const balances = useMemo(() => {
    const cf = Number(informacion?.cf ?? 0) + Number(informacion?.CF ?? 0); // legacy CF
    const hueso = Number(informacion?.hueso ?? 0);
    const viphueso = Number(informacion?.viphueso ?? 0);

    return {
      cf: Number.isFinite(cf) ? cf : 0,
      hueso: Number.isFinite(hueso) ? hueso : 0,
      viphueso: Number.isFinite(viphueso) ? viphueso : 0,
    };
  }, [informacion?.cf, informacion?.CF, informacion?.hueso, informacion?.viphueso]);

  // =========================
  // Helpers
  // =========================
  function normalizeKey(k) {
    return String(k || "").trim();
  }

  function isIgnoredKey(keyLower) {
    return keyLower === "time" || keyLower === "tiempo";
  }

  function validateReqsDetailed(reqObj) {
    if (!reqObj || typeof reqObj !== "object") return { ok: true, missing: [] };

    const missing = [];

    for (const [rawKey, rawNeed] of Object.entries(reqObj)) {
      const key = normalizeKey(rawKey);
      if (!key) continue;

      const lower = key.toLowerCase();

      if (isIgnoredKey(lower)) continue;

      // ignora strings tipo "Max"
      if (typeof rawNeed === "string" && rawNeed.toLowerCase() === "max") continue;

      const needNum = Number(rawNeed);
      if (!Number.isFinite(needNum) || needNum <= 0) continue;

      // ✅ fuera de inventario
      if (lower === "cf") {
        const have = balances.cf;
        if (have < needNum) missing.push({ item: "CF", need: needNum, have, falta: needNum - have });
        continue;
      }
      if (lower === "hueso") {
        const have = balances.hueso;
        if (have < needNum)
          missing.push({ item: "HUESO", need: needNum, have, falta: needNum - have });
        continue;
      }
      if (lower === "viphueso") {
        const have = balances.viphueso;
        if (have < needNum)
          missing.push({ item: "VIPHUESO", need: needNum, have, falta: needNum - have });
        continue;
      }

      // ✅ inventario
      const have = Number(invMap.get(key) || 0);
      if (have < needNum) missing.push({ item: key, need: needNum, have, falta: needNum - have });
    }

    return { ok: missing.length === 0, missing };
  }

  function formatMissingMessage(missingArr, title = "FALTAN RECURSOS") {
    const lines = missingArr.map(
      (m) => `• ${m.item}: tienes ${m.have} / necesitas ${m.need} → faltan ${m.falta}`
    );
    return `${title}\n${lines.join("\n")}`;
  }

  // =========================
  // ✅ Handler
  // =========================
  const subirNivel = async () => {
    try {
      // Para land necesitas id. Para molino el backend puede no usarlo,
      // pero igual lo pasamos por compatibilidad.
      if (!id && !isMolino) {
        setError?.(true);
        setMensaje?.("ID_INVALID");
        setOpen?.(true);
        return;
      }

      // ✅ Validación previa (si reqs se pasa desde el UI)
      const check = validateReqsDetailed(reqs);
      if (!check.ok) {
        setError?.(true);
        setMensaje?.(formatMissingMessage(check.missing));
        setOpen?.(true);
        return;
      }

      setLoading(true);

      // ✅ Selector correcto de endpoint:
      const resp = await subirNivelServices(id, { mode: isMolino ? "molino" : "land" });

      // ✅ Molino backend => { ok, usuario, molinoNivel, upgrade }
      // ✅ Land backend => puede ser usuario directo o { data/usuario }
      const usuarioActualizado = resp?.usuario ?? resp?.data ?? resp;

      if (!usuarioActualizado) throw new Error("INVALID_RESPONSE");

      dispatch(setInformacion(usuarioActualizado));

      setError?.(false);
      setMensaje?.("SUCCESS");
      setOpen?.(true);
    } catch (error) {
      console.log("LVL UP ERROR:", error);

      setError?.(true);
      setOpen?.(true);

      const backendErr = error?.response?.data;

      if (backendErr?.missing && Array.isArray(backendErr.missing)) {
        setMensaje?.(formatMissingMessage(backendErr.missing, backendErr?.error || "FALTAN RECURSOS"));
      } else {
        setMensaje?.(backendErr?.error || error?.message || "ERROR");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button className="rpgui-button" onClick={subirNivel} disabled={loading}>
      <p>{loading ? "..." : "LVL UP"}</p>
    </Button>
  );
};
